import {
  DEFAULT_FINANCE_APR,
  FINANCE_TERM_STEP_MONTHS,
  PLAN_MAX_FINANCE_TERM_MONTHS,
  MIN_FINANCE_TERM_MONTHS,
  amortisedMonthly,
  formatTermLabel,
} from "@/utils/financePayment";
import { formatGbp } from "@/utils/planViewModel";

const TERMS = Array.from(
  { length: (PLAN_MAX_FINANCE_TERM_MONTHS - MIN_FINANCE_TERM_MONTHS) / FINANCE_TERM_STEP_MONTHS + 1 },
  (_, index) => MIN_FINANCE_TERM_MONTHS + index * FINANCE_TERM_STEP_MONTHS,
);

export default function PlanFinance({
  price,
  paymentType,
  onPaymentType,
  termMonths,
  onTermMonths,
  annualBenefit,
  disclosure,
}: {
  price: number;
  paymentType: "upfront" | "finance";
  onPaymentType: (type: "upfront" | "finance") => void;
  termMonths: number;
  onTermMonths: (months: number) => void;
  annualBenefit: number | null;
  disclosure: string | null;
}) {
  const monthly = amortisedMonthly(price, DEFAULT_FINANCE_APR, termMonths);
  const termIndex = Math.max(0, TERMS.indexOf(termMonths));

  return (
    <div className="flex flex-col gap-6 rounded-[28px] border border-powlo-border bg-white p-6 sm:p-8">
      <div role="group" aria-label="How to pay" className="flex gap-2">
        <TabButton active={paymentType === "upfront"} onClick={() => onPaymentType("upfront")}>
          Pay in full
        </TabButton>
        <TabButton active={paymentType === "finance"} onClick={() => onPaymentType("finance")}>
          Spread the cost
        </TabButton>
      </div>

      {paymentType === "upfront" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MiniStat label="Fully installed" value={formatGbp(price)} />
          {annualBenefit != null && <MiniStat label="Year-1 benefit" value={formatGbp(annualBenefit)} />}
        </div>
      )}

      {paymentType === "finance" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-powlo-border bg-[#FCFBF8] p-5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-bold">Over how long</span>
              <span className="font-powlo-display text-2xl font-bold tracking-tight">{formatTermLabel(termMonths)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={TERMS.length - 1}
              step={1}
              value={termIndex < 0 ? TERMS.length - 1 : termIndex}
              onChange={(event) => onTermMonths(TERMS[Number(event.target.value)] ?? termMonths)}
              className="mt-3 w-full accent-powlo-indigo"
              aria-label="Repayment term"
            />
            <div className="mt-1 flex justify-between text-xs text-powlo-muted-light">
              <span>{formatTermLabel(TERMS[0])}</span>
              <span>{formatTermLabel(TERMS[TERMS.length - 1])}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-2xl bg-powlo-navy p-6 text-white">
            <div className="text-sm text-[#C9D1DE]">Monthly payment</div>
            <div className="font-powlo-display text-5xl font-bold tracking-tight text-powlo-lime">{formatGbp(monthly)}</div>
            <div className="text-sm text-[#C9D1DE]">
              {termMonths} months on {formatGbp(price)}
            </div>
            {annualBenefit != null && (
              <div className="text-sm text-[#C9D1DE]">Modelled year-1 benefit {formatGbp(annualBenefit)}.</div>
            )}
          </div>
        </div>
      )}

      <p className="m-0 text-xs leading-relaxed text-powlo-muted-light">
        Representative 10.9% APR. Credit is subject to status.
        {disclosure ? ` ${disclosure}` : ""}
      </p>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-11 rounded-full px-5 text-sm font-bold ${active ? "bg-powlo-ink text-white" : "bg-powlo-cream text-powlo-ink"}`}
    >
      {children}
    </button>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-powlo-border p-4">
      <div className="text-xs text-powlo-muted">{label}</div>
      <div className="font-powlo-display text-2xl font-bold">{value}</div>
    </div>
  );
}
